import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { AppHeader } from "@/components/AppHeader";
import { renderMarkdown } from "@/lib/renderMarkdown";
import { MathTools, makeInsertHandler } from "@/components/MathTools";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  FileText,
  RefreshCw,
  Sparkles,
  PenSquare,
  CheckCircle2,
  XCircle,
  Loader2,
  Send,
} from "lucide-react";

const ARABIC_TO_ROMAN = { "9": "IX", "10": "X", "11": "XI", "12": "XII" };
const PREFIX_TO_SLUG = { comp: "computer", eng: "english", math: "mathematics" };

function PaperQuestion({ q, index, topicId }) {
  const [openSolution, setOpenSolution] = useState(false);
  const [openAttempt, setOpenAttempt] = useState(false);
  const [attempt, setAttempt] = useState("");
  const [grading, setGrading] = useState(false);
  const [result, setResult] = useState(null);
  const attemptRef = useRef(null);
  const marks = q.marks || 2;

  const submitAttempt = async () => {
    if (!attempt.trim()) {
      toast.error("Write your answer first");
      return;
    }
    setGrading(true);
    setResult(null);
    try {
      const { data } = await api.post(`/past-papers/grade`, {
        topic_id: topicId,
        question_index: index,
        attempt,
      });
      setResult(data);
      toast.success(`${data.marks_awarded}/${data.max_marks} — ${data.verdict}`);
    } catch (e) {
      if (!e.isSubscriptionError) {
        toast.error(e?.response?.data?.detail || "Grading failed");
      }
    } finally {
      setGrading(false);
    }
  };

  return (
    <div
      data-testid={`paper-question-${index}`}
      className="tactile-card p-5 sm:p-6"
    >
      <div className="flex items-start gap-3 mb-3">
        <div className="w-9 h-9 rounded-xl bg-[hsl(var(--brimi-blue))]/10 text-[hsl(var(--brimi-blue))] flex items-center justify-center font-black shrink-0">
          {index + 1}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-muted-foreground mb-1">
            <FileText className="w-3 h-3" />
            Question {index + 1}
            <span className="ml-2 px-2 py-0.5 rounded-full bg-[hsl(var(--brimi-yellow))]/30 text-foreground">
              {marks} mark{marks !== 1 ? "s" : ""}
            </span>
          </div>
          <div className="text-base sm:text-lg leading-relaxed">
            {renderMarkdown(q.question)}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setOpenAttempt((o) => !o)}
          data-testid={`paper-attempt-toggle-${index}`}
          className="pill-btn bg-[hsl(var(--brimi-coral))] hover:bg-[hsl(var(--brimi-coral))]/90 text-white text-xs sm:text-sm px-3 py-1.5"
        >
          <PenSquare className="w-4 h-4" />
          {openAttempt ? "Close attempt" : "Attempt this question"}
        </button>
        <button
          type="button"
          onClick={() => setOpenSolution((o) => !o)}
          data-testid={`paper-solution-toggle-${index}`}
          className="pill-btn bg-foreground/5 hover:bg-foreground/10 text-foreground text-xs sm:text-sm px-3 py-1.5"
        >
          {openSolution ? (
            <>
              <ChevronUp className="w-4 h-4" /> Hide solution
            </>
          ) : (
            <>
              <ChevronDown className="w-4 h-4" /> Show solution
            </>
          )}
        </button>
      </div>

      {openAttempt && (
        <div
          data-testid={`paper-attempt-${index}`}
          className="mt-3 p-4 rounded-2xl bg-[hsl(var(--brimi-coral))]/5 border border-[hsl(var(--brimi-coral))]/20"
        >
          <div className="text-xs uppercase tracking-widest font-bold text-muted-foreground mb-2">
            Write your answer
          </div>
          <textarea
            data-testid={`paper-attempt-input-${index}`}
            ref={attemptRef}
            value={attempt}
            onChange={(e) => setAttempt(e.target.value)}
            placeholder="Type your full working here — formulas, steps, final answer with units. Marks are awarded for each correct step, so show your method."
            rows={5}
            className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:border-[hsl(var(--brimi-coral))] focus:ring-2 focus:ring-[hsl(var(--brimi-coral))]/20 resize-y min-h-[120px] max-h-[40vh]"
          />
          <div className="mt-2 flex items-center justify-between gap-2 flex-wrap">
            <span className="text-xs text-muted-foreground">
              {attempt.length} / 4000 chars
            </span>
            <div className="flex items-center gap-2">
              <MathTools insertText={makeInsertHandler(attemptRef, setAttempt)} />
              <button
                type="button"
                onClick={submitAttempt}
                disabled={grading || !attempt.trim()}
                data-testid={`paper-attempt-submit-${index}`}
                className="pill-btn bg-[hsl(var(--brimi-blue))] hover:bg-[hsl(var(--brimi-blue))]/90 text-white text-xs sm:text-sm px-4 py-1.5 disabled:opacity-50"
              >
                {grading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                {grading ? "Examiner grading…" : "Submit for marking"}
              </button>
            </div>
          </div>

          {result && (
            <div
              data-testid={`paper-attempt-result-${index}`}
              className="mt-3 p-3 rounded-xl bg-white border-2 border-[hsl(var(--brimi-mint))]/40"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="text-2xl font-black">
                  {result.marks_awarded} / {result.max_marks}
                  <span className="ml-2 text-sm font-bold uppercase tracking-wider text-[hsl(var(--brimi-blue))]">
                    · {result.verdict}
                  </span>
                </div>
              </div>
              {result.correct_points?.length > 0 && (
                <div className="mb-2">
                  <div className="text-xs uppercase tracking-widest font-bold text-[hsl(var(--brimi-blue))] mb-1">
                    What you got right
                  </div>
                  <ul className="text-sm space-y-1">
                    {result.correct_points.map((p, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-[hsl(var(--brimi-mint))] shrink-0 mt-0.5" />
                        <span>{renderMarkdown(p)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {result.missing_points?.length > 0 && (
                <div className="mb-2">
                  <div className="text-xs uppercase tracking-widest font-bold text-[hsl(var(--brimi-coral))] mb-1">
                    What's missing
                  </div>
                  <ul className="text-sm space-y-1">
                    {result.missing_points.map((p, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <XCircle className="w-4 h-4 text-[hsl(var(--brimi-coral))] shrink-0 mt-0.5" />
                        <span>{renderMarkdown(p)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {result.feedback && (
                <div className="text-sm italic text-foreground/80">
                  {renderMarkdown(result.feedback)}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {openSolution && (
        <div
          data-testid={`paper-solution-${index}`}
          className="mt-3 p-4 rounded-2xl bg-[hsl(var(--brimi-mint))]/15 border border-[hsl(var(--brimi-mint))]/30 text-sm sm:text-base leading-relaxed"
        >
          <div className="text-xs uppercase tracking-widest font-bold text-muted-foreground mb-2 flex items-center gap-2">
            <Sparkles className="w-3 h-3 text-[hsl(var(--brimi-coral))]" />
            Step-by-step solution
          </div>
          {(q.solution || "")
            .split(/\n+/)
            .filter(Boolean)
            .map((line, i) => (
              <div key={i} className="mb-1">
                {renderMarkdown(line)}
              </div>
            ))}
        </div>
      )}
    </div>
  );
}

export default function PastPapersPage() {
  const { topicId } = useParams();
  const { user } = useAuth();
  const isOwner = !!user?.is_owner;
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [regenerating, setRegenerating] = useState(false);

  const load = () => {
    setLoading(true);
    api
      .get(`/past-papers/${topicId}`)
      .then((r) => setData(r.data))
      .catch((e) => {
        if (!e.isSubscriptionError) {
          toast.error(e?.response?.data?.detail || "Couldn't load papers");
        }
      })
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [topicId]);

  const regenerate = async () => {
    if (!window.confirm("Re-roll all 6 questions? This burns a fresh AI call.")) return;
    setRegenerating(true);
    try {
      const { data: fresh } = await api.post(`/past-papers/${topicId}/regenerate`);
      setData(fresh);
      toast.success("Fresh questions generated.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Regenerate failed");
    } finally {
      setRegenerating(false);
    }
  };

  const m = (topicId || "").match(/^([a-z]+)-(\d+)/);
  const backTo = m
    ? `/subjects/${PREFIX_TO_SLUG[m[1]] || m[1]}/classes/${ARABIC_TO_ROMAN[m[2]] || m[2]}/topics`
    : "/subjects";

  return (
    <div className="min-h-screen">
      <AppHeader />
      <main className="max-w-3xl mx-auto px-6 py-12">
        <Link
          to={backTo}
          data-testid="papers-back-link"
          className="inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground mb-8"
        >
          <ArrowLeft className="w-4 h-4" /> Back to chapters
        </Link>

        <div className="mb-8">
          <div className="text-sm font-bold uppercase tracking-widest text-[hsl(var(--brimi-blue))] mb-3 inline-flex items-center gap-2">
            <FileText className="w-4 h-4" />
            {data?.board || "Board"}-style Past Year Papers
          </div>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tight mb-2">
            {data?.topic_title || "Practice paper"}
          </h1>
          <p className="text-sm text-muted-foreground">
            AI-generated board-style questions modelled on recent{" "}
            <span className="font-semibold">{data?.board || "ICSE/ISC"}</span>{" "}
            {data?.subject || "Board"} papers for Class{" "}
            {data?.class_id || "—"} — with full step-by-step solutions.
          </p>
        </div>

        {isOwner && data && (
          <div className="mb-6">
            <button
              type="button"
              onClick={regenerate}
              disabled={regenerating}
              data-testid="papers-regenerate-btn"
              className="pill-btn bg-[hsl(var(--brimi-yellow))] hover:bg-[hsl(var(--brimi-yellow))]/90 text-foreground text-xs sm:text-sm px-4 py-2 disabled:opacity-60"
            >
              <RefreshCw
                className={`w-4 h-4 ${regenerating ? "animate-spin" : ""}`}
              />
              {regenerating ? "Regenerating…" : "Regenerate (owner)"}
            </button>
          </div>
        )}

        {loading ? (
          <div className="tactile-card p-10 text-center text-muted-foreground">
            <Sparkles className="w-6 h-6 mx-auto mb-3 animate-pulse" />
            Generating board-style questions — this can take ~20 seconds the first
            time for a chapter…
          </div>
        ) : !data?.questions?.length ? (
          <div className="tactile-card p-10 text-center text-muted-foreground">
            No questions available yet. Try regenerating.
          </div>
        ) : (
          <div className="space-y-4">
            {data.questions.map((q, i) => (
              <PaperQuestion key={i} q={q} index={i} topicId={topicId} />
            ))}
          </div>
        )}

        <div className="mt-10 text-xs text-muted-foreground bg-foreground/5 rounded-2xl p-4">
          <strong>Note:</strong> Questions are AI-generated to match the format,
          difficulty and marking scheme of CISCE board papers — they are{" "}
          <em>not</em> verbatim past-year questions. Treat them as high-quality
          practice problems and verify final answers with your teacher.
        </div>
      </main>
    </div>
  );
}
